import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AllProducts = () => {
  const { authHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`, { headers: authHeader }),
      axios.get(`${API}/categories`),
    ]).then(([pRes, cRes]) => {
      setProducts(pRes.data);
      setCategories(cRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers: authHeader });
      setProducts(prev => prev.filter(p => p._id !== id));
      toast.success('Product deleted');
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      const { data } = await axios.put(`${API}/products/${product._id}`, { isActive: !product.isActive }, { headers: authHeader });
      setProducts(prev => prev.map(p => p._id === product._id ? { ...p, isActive: data.isActive } : p));
      toast.success(data.isActive ? 'Product activated' : 'Product deactivated');
    } catch {
      toast.error('Failed to update product');
    }
  };

  const filtered = products.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || p.category?._id === filterCat || p.category?.name === filterCat;
    return matchSearch && matchCat;
  });

  return (
    <div className="p-6" data-testid="admin-all-products">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">All Products</h1>
          <p className="text-[#7A6A63] text-sm">{products.length} products total</p>
        </div>
        <Link
          to="/admin/products/new"
          className="flex items-center gap-2 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"
          data-testid="add-product-button"
        >
          <Plus size={16} /> Add New Product
        </Link>
      </div>

      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A63]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-8 pr-3 py-2 border border-[#ECE0DA] rounded-lg text-sm focus:outline-none focus:border-[#7A1125]"
            data-testid="product-search"
          />
        </div>
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          className="px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm focus:outline-none focus:border-[#7A1125] text-[#7A6A63]"
          data-testid="product-filter-category"
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-[#ECE0DA] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FDF8F3] border-b border-[#ECE0DA]">
                  {['Image', 'Name', 'Category', 'Price', 'Cost Price', 'Stock', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#7A6A63] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#ECE0DA]">
                {filtered.map(p => (
                  <tr key={p._id} className="hover:bg-[#FDF8F3] transition-colors" data-testid={`product-row-${p._id}`}>
                    <td className="px-4 py-3">
                      <img
                        src={p.images?.[0] || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=60'}
                        alt={p.name}
                        className="w-10 h-10 rounded-lg object-cover border border-[#ECE0DA]"
                        onError={e => { e.target.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=60'; }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#1F1410] line-clamp-2 max-w-[160px]">{p.name}</p>
                      {p.badge && <span className="text-xs text-[#7A1125]">{p.badge}</span>}
                    </td>
                    <td className="px-4 py-3 text-[#7A6A63]">{p.category?.name || '—'}</td>
                    <td className="px-4 py-3 font-semibold text-[#7A1125]">
                      {p.pricingType === 'sizewise' ? `From ₹${Math.min(...(p.sizes || []).map(s => s.price))}` : `₹${p.price}`}
                    </td>
                    <td className="px-4 py-3 text-[#7A6A63]" data-testid={`product-cost-price-${p._id}`}>₹{p.costPrice ?? 0}</td>
                    <td className="px-4 py-3 text-[#7A6A63]">
                      {p.pricingType === 'sizewise' ? (p.sizes || []).reduce((s, x) => s + x.stock, 0) : p.stock}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.isActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => handleToggleActive(p)} className="p-1.5 rounded-lg hover:bg-[#FDF8F3] text-[#7A6A63] hover:text-[#7A1125] transition-colors" data-testid={`toggle-product-${p._id}`}>
                          {p.isActive ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <Link to={`/admin/products/${p._id}/edit`} className="p-1.5 rounded-lg hover:bg-[#FDF8F3] text-[#7A6A63] hover:text-[#7A1125] transition-colors" data-testid={`edit-product-${p._id}`}>
                          <Edit size={14} />
                        </Link>
                        <button onClick={() => handleDelete(p._id)} className="p-1.5 rounded-lg hover:bg-red-50 text-[#7A6A63] hover:text-red-500 transition-colors" data-testid={`delete-product-${p._id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllProducts;
